import React, { useContext, useRef, useEffect } from 'react';
import { arrayOf, shape, string } from 'prop-types';
import { Tooltip } from '@thd-olt-component-react/tooltip';
import { Alert } from '@thd-olt-component-react/alert';
import { Row } from '@thd-olt-component-react/grid';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useProduct, useAisleBay } from '@thd-nucleus/data-sources';
import { useProductDeprecated, useAisleBayDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import AisleBayTextMe from './AisleBayTextMe';
import { dataModel } from './dataModel';
import {
  formatAisleBayResponse,
  publish,
  shouldDisplayAisleBay,
  getAisleBayInventory,
  getStoreLayoutLink,
  getBOPISFulfillment
} from '../helper';
import { AisleBayFindMost } from './AisleBayFindMost';

const AisleBay = (props) => {
  const textMeTooltipRef = useRef(null);
  const { itemId, products = [] } = props;

  if (!(products.length || itemId)) {
    return null;
  }

  const {
    channel,
    store
  } = useContext(ExperienceContext);
  const domains = ['identifiers', 'availabilityType', 'fulfillment'];
  const { storeId, storeName } = useStore();
  if (products.length) {
    return <AisleBayFindMost products={products} store={store} itemId={itemId} />;
  }

  const { data, loading, error } = useProductDeprecated({
    itemId,
    storeId,
    domains,
    skip: storeId === '8119',
    ssr: false
  }, 'clientOnlyProduct');

  const storeSkuid = data?.product?.identifiers?.storeSkuNumber;
  const storeSkuIds = storeSkuid ? [storeSkuid] : [];
  const { fulfillment = {}, availabilityType = {} } = data?.product || {};
  const type = availabilityType?.type;
  const { productType } = data?.product?.identifiers || '';

  const skipAisleBay = !storeSkuid || !shouldDisplayAisleBay(fulfillment, type);
  const [makeRequest, aisleBayResponse] = useAisleBayDeprecated({
    storeId,
    storeSkuIds,
    lazy: true
  }, 'aislebay');

  useEffect(() => {
    if (!skipAisleBay || productType === 'MAJOR_APPLIANCE') {
      makeRequest();
    }
  }, [skipAisleBay, productType]);

  if (!data || loading || (error && !data)) {
    return null;
  }

  if (!aisleBayResponse.data || aisleBayResponse.loading || (aisleBayResponse.error && !aisleBayResponse.data)) {
    return null;
  }
  const storeSkus = aisleBayResponse.data?.aislebay?.storeSkus || [];
  const aisleBay = storeSkus[0]?.aisleBayInfo || {};
  const aisleBayMessage = formatAisleBayResponse({ response: aisleBay });
  const aisle = aisleBay?.aisle;
  const bay = aisleBay?.bay;

  if (aisleBayMessage === '') {
    return null;
  }
  const bopisFulfillment = getBOPISFulfillment(fulfillment);
  const localStore = (bopisFulfillment?.locations || []).find((location) => location.isAnchor);
  const onDisplayForMajorAppliances = productType === 'MAJOR_APPLIANCE' && !fulfillment.isBopis;
  const aisleBayInventory = getAisleBayInventory(localStore, onDisplayForMajorAppliances);

  function buildStoreMapLink() {
    let storeMapLink = null;
    if (channel === 'mobile') {
      storeMapLink = (
        <div className="aislebay-wrapper__store-map">
          <a href={getStoreLayoutLink({ aislebay: aisleBay, itemId, store })}>
            <img
              className="aislebay-wrapper__store-map__icon"
              alt="pin icon"
              src="https://assets.homedepot-static.com/images/v1/pin-orange.svg"
            />
            <span className="u__default-link"> Store Map</span>
          </a>
        </div>
      );
    }
    return storeMapLink;
  }

  return (
    <div className="aislebay-wrapper">
      <fieldset className="aislebay-wrapper--fieldset">
        <legend className="aislebay-wrapper--title">{localStore?.storeName} Store</legend>
        <Row>
          {(localStore?.inventory?.quantity || onDisplayForMajorAppliances) && (
            <div className="aislebay-wrapper--alert">
              <Alert type="success" inline>
                <span className="aislebay-wrapper--inventory">{aisleBayInventory}</span>
              </Alert>
            </div>
          )}
          {onDisplayForMajorAppliances && (
            <div className="aislebay-wrapper--alert">|</div>
          )}
          <div>
            <span className="aislebay-wrapper--message">
              {onDisplayForMajorAppliances ? `${storeName} Store` : aisleBayMessage}
            </span>
            {!onDisplayForMajorAppliances && (
              <Tooltip
                channel={channel}
                closeButton
                placement="bottom"
                content={(
                  <AisleBayTextMe
                    itemId={itemId}
                    storeId={storeId}
                    aisleBayText={aisleBayMessage}
                    ref={textMeTooltipRef}
                  />
                )}
              >
                {/* eslint-disable-next-line */}
                <a
                  className="u__default-link"
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    publish('open');
                    if (textMeTooltipRef?.current) {
                      textMeTooltipRef.current.resetState();
                    }
                  }}
                >
                  Text to Me
                </a>
              </Tooltip>
            )}
          </div>
        </Row>
      </fieldset>
      {buildStoreMapLink()}
    </div>
  );
};

AisleBay.displayName = 'AisleBay';

AisleBay.propTypes = {
  /** An Item that has product highlights */
  itemId: string,
  /** Support for Search / Browse generic Aisle & Bay information */
  products: arrayOf(shape())
};

AisleBay.defaultProps = {
  itemId: null,
  products: []
};

AisleBay.defaultProps = {};

AisleBay.dataModel = dataModel;

export { AisleBay };
